import React, { Dispatch, FC, SetStateAction } from 'react';

import IProduct from 'models/product';
import InputLabel from 'components/inputfields/InputLabel';
import { InputTextFieldDebounced } from 'components/inputfields/InputTextField';
import { FloatInputDebounced } from '../FloatInput';
import ListDropDown from 'components/inputfields/ListDropDown';
import BooleanField from 'components/inputfields/BooleanField';
import MultipleSelect from 'components/inputfields/MultipleSelect';
import { batteryHazardousMetals, batteryTechnologies, batteryTypes } from 'components/constants-ts';
import ThreeWaySwitch from 'components/inputfields/ThreeWaySwitch';
import { useIsFieldRequired } from '../validation-context/ProductValidationContext';
import { getProductFieldVisibility } from '../utils/productFieldVisibility';

interface BatteriesProps {
  readOnly?: boolean;
  state: IProduct;
  approve?: boolean;
  setState: Dispatch<SetStateAction<IProduct>>;
}

const Batteries: FC<BatteriesProps> = ({ readOnly, approve, state, setState }) => {
  const isBatteryTypeOtherRequired = useIsFieldRequired('batteryTypeOther');
  const { fields } = getProductFieldVisibility({ product: state });
  const handleDropdownChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const stateName = event.target.name;
    const stateValue = event.target.value;
    const val = !stateValue || stateValue === '' ? null : stateValue;
    stateName && setState({ ...state, [stateName]: val } as IProduct);
  };

  const fieldStyle = { marginBottom: 0, width: 200, flex: 'none' };

  return (
    <>
      <h3>Specifikation</h3>

      <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <InputLabel heading="Batterimodell" text="Exempelvis Ultra Power eller Energizer Plus." />
          <InputTextFieldDebounced<IProduct>
            readOnly={readOnly}
            inputName="batteryModel"
            state={state}
            setState={setState}
            style={fieldStyle}
          />

          <InputLabel heading="Batterityp (Storlek)" text="Batteriets storlek/typ, t.ex. AAA." />
          <ListDropDown
            readOnly={readOnly}
            disableLabel
            displayEmpty
            targetName="batteryType"
            listData={batteryTypes}
            value={state.batteryType}
            handleChange={handleDropdownChange}
            style={{ ...fieldStyle }}
          />

          {fields.batteryTypeOther && (
            <div>
              <InputLabel heading="Annan batterityp" labelStyles={{ marginTop: 0 }} required={isBatteryTypeOtherRequired} />
              <InputTextFieldDebounced<IProduct>
                readOnly={readOnly}
                inputName="batteryTypeOther"
                state={state}
                setState={setState}
                style={fieldStyle}
              />
            </div>
          )}

          <InputLabel heading="Spänning" text="Spänning angiven i volt (V)." />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              name="batteryVoltage"
              state={state}
              setState={setState}
              style={fieldStyle}
            />
            <h3 style={{ margin: 0 }}>&nbsp;V</h3>
          </div>

          <InputLabel heading="Kemisk sammansättning (Batteriteknik)" text="Batteriteknik, t.ex. alkaliskt." />
          <ListDropDown
            readOnly={readOnly}
            disableLabel
            displayEmpty
            targetName="batteryTechnology"
            listData={batteryTechnologies}
            value={state.batteryTechnology}
            handleChange={handleDropdownChange}
            style={fieldStyle}
          />

          <InputLabel
            heading="Kapacitet"
            text="Batteriets kapacitet angiven i milliamperetimmar (mAh). Obligatoriskt för laddningsbara batterier."
          />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              name="batteryCapacity"
              state={state}
              setState={setState}
              style={fieldStyle}
            />
            <h3 style={{ margin: 0 }}>&nbsp;mAh</h3>
          </div>

          <InputLabel
            heading="Batteritid"
            text="Minsta genomsnittliga batteritid angiven i timmar (h), t.ex. 500 h. Obligatoriskt för engångsbatterier."
          />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              name="batteryLife"
              state={state}
              setState={setState}
              style={fieldStyle}
            />
            <h3 style={{ margin: 0 }}>&nbsp;h</h3>
          </div>

          <InputLabel heading="Batteriets vikt" text="Vikt per batteri angivet i gram (g)." />
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <FloatInputDebounced<IProduct>
              readOnly={readOnly}
              dense={false}
              name="batteryWeight"
              state={state}
              setState={setState}
              style={fieldStyle}
            />
            <h3 style={{ margin: 0 }}>&nbsp;g</h3>
          </div>

          <InputLabel
            heading="Laddningsbar"
            text={`Är batteriet laddningsbart? Om Nej visas "Engångsbatteri, ej laddningsbart" i den slutliga produktinformationen.`}
          />
          <BooleanField readOnly={readOnly} name="batteryRechargeable" state={state} setState={setState} />

          <InputLabel heading="Tillverkningsort" text="Ort för batteritillverkningsanläggningen, t.ex. 'Shenzhen'." />
          <InputTextFieldDebounced<IProduct>
            readOnly={readOnly}
            inputName="manufacturingLocation"
            state={state}
            setState={setState}
            style={fieldStyle}
          />

          <InputLabel
            heading="Innehåller kvicksilver, kadmium eller bly"
            text="Markera de metaller batteriet ska märkas med. Ett batteri ska märkas med: Hg om det innehåller mer än 0,0005 viktprocent kvicksilver, Cd om mer än 0,002 viktprocent kadmium, Pb om mer än 0,004 viktprocent bly. (EU:s batteridirektiv 2006/66/EG art. 21.)"
          />
          <MultipleSelect
            readOnly={readOnly}
            disableLabel
            listData={batteryHazardousMetals}
            value={state?.batteryHazardousMetals ?? []}
            targetName="batteryHazardousMetals"
            handleChange={handleDropdownChange}
            style={fieldStyle}
          />
        </div>
        {approve && state?.approve?.find(x => x.field === 'battery') && (
          <ThreeWaySwitch
            readOnly={readOnly}
            state={state}
            setState={setState}
            name={"battery" as keyof IProduct}
          />
        )}
      </div>
    </>
  );
};

export default Batteries;
